import type { FunctionComponent, RefObject } from 'react';
import type { ClientPackageChapterDto } from '../../api/generated/client';
import type { ChartExporterChapterName } from '../../config/client-package-chart-exporter';
import { isChartExporterChapterName } from '../../config/client-package-chart-exporter';
import { DEFAULT_TIME_RANGE } from '../../config/constants';
import { ClientOutputLifeEventsScreen } from '../../container/ClientOutputLifeEventsScreen';
import { ClientPackageChapterName } from '../../enums';
import type { AvailableLanguage } from '../../i18n/i18nextInit';
import type { IChapterImageDom } from '../../interfaces/package';
import type { IExporterProps } from '../exporter/Exporter';
import { Exporter } from '../exporter/Exporter';
import { ClientPackageCashflow } from '../exporter/scenarios/ClientPackageCashflow';
import type { IClientPackageFamilyTreeRef } from '../exporter/scenarios/ClientPackageFamilyTree';
import { ClientPackageFamilyTree } from '../exporter/scenarios/ClientPackageFamilyTree';
import { ClientPackageLiquidity } from '../exporter/scenarios/ClientPackageLiquidity';
import { ClientPackageMonteCarloProjection } from '../exporter/scenarios/ClientPackageMonteCarloProjection';

const chartExporterMap: Record<ChartExporterChapterName, Omit<IExporterProps, 'language' | 'onImageRendering'>> = {
    [ClientPackageChapterName.PROJECTION]: {
        chapterName: ClientPackageChapterName.PROJECTION,
        componentToRender: <ClientPackageMonteCarloProjection />,
        height: 1200,
    },
    [ClientPackageChapterName.EVENTS_0]: {
        chapterName: ClientPackageChapterName.EVENTS_0,
        componentToRender: <ClientOutputLifeEventsScreen />,
    },
    [ClientPackageChapterName.CASHFLOW]: {
        chapterName: ClientPackageChapterName.CASHFLOW,
        componentToRender: <ClientPackageCashflow isSecondPage={false} />,
        width: 2000,
        height: 1000,
    },
    [ClientPackageChapterName.CASHFLOW2]: {
        chapterName: ClientPackageChapterName.CASHFLOW2,
        componentToRender: <ClientPackageCashflow isSecondPage={true} />,
        width: 2000,
        height: 1000,
    },
    [ClientPackageChapterName.FAMILY_TREE]: {
        chapterName: ClientPackageChapterName.FAMILY_TREE,
        componentToRender: (ref: RefObject<IClientPackageFamilyTreeRef | null>) => <ClientPackageFamilyTree ref={ref} />,
        width: 2000,
        height: 1200,
    },
    [ClientPackageChapterName.LIQUIDITY]: {
        chapterName: ClientPackageChapterName.LIQUIDITY,
        componentToRender: <ClientPackageLiquidity />,
        width: 2300,
        height: 950,
    },
};

interface IProps {
    clientPackageChapters: ClientPackageChapterDto[];
    onImageRendering: (imagePromise: IChapterImageDom) => void;
    availableLanguage: AvailableLanguage;
    projectionYears: number;
}

export const ChapterExporter: FunctionComponent<IProps> = ({ clientPackageChapters, onImageRendering, availableLanguage, projectionYears }) => (
    <div>
        {clientPackageChapters.map((chapter) =>
            chapter.selected && isChartExporterChapterName(chapter.chapterName) ? (
                <>
                    <Exporter
                        language={availableLanguage}
                        onImageRendering={onImageRendering}
                        key={chapter.chapterName}
                        {...chartExporterMap[chapter.chapterName]}
                    />
                    {chapter.chapterName === ClientPackageChapterName.CASHFLOW && projectionYears > DEFAULT_TIME_RANGE && (
                        <Exporter
                            language={availableLanguage}
                            onImageRendering={onImageRendering}
                            key={ClientPackageChapterName.CASHFLOW2}
                            {...chartExporterMap[ClientPackageChapterName.CASHFLOW2]}
                        />
                    )}
                </>
            ) : null,
        )}
    </div>
);
